import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './sidebar.css';

// Import icons
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  Package,
  MessageSquare,
  Building,
  FileText,
  Mail,
  HelpCircle,
  Bell,
  ChevronDown,
  ChevronRight,
  Calendar,
  Database,
  Shield,
  Briefcase,
  Globe,
  CreditCard,
  FileCode,
  Layers,
  PieChart,
  Home,
  UserCog,
  FolderOpen,
  ChartBar,
  Webhook
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  badge?: number | string;
  badgeColor?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  isNew?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen , onToggle}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard size={20} />,
      path: '/admin/dashboard'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Users size={20} />,
      children: [
        {
          id: 'all-users',
          label: 'All Users',
          icon: <Users size={16} />,
          path: '/admin/users-list',
          badge: 124,
          badgeColor: 'primary'
        },
        {
          id: 'roles',
          label: 'Roles & Permissions',
          icon: <Shield size={16} />,
          path: '/admin/roles'
        },
        // {
        //   id: 'activity',
        //   label: 'User Activity',
        //   icon: <BarChart3 size={16} />,
        //   path: '/admin/users/activity'
        // }
      ]
    },
    {
      id: 'fdc',
      label: 'FDC Projects',
      icon: <FileText size={20} />,
      path: '/data-centers/fdc/projects'
    },
    {
      id: 'webhook-logs',
      label: 'Webhook Logs',
      icon: <Webhook size={20} />,
      path: '/admin/webhook-logs'
    },
    // {
    //   id: 'companies',
    //   label: 'Companies',
    //   icon: <Building size={20} />,
    //   children: [
    //     {
    //       id: 'b2b',
    //       label: 'B2B Companies',
    //       icon: <Building size={16} />,
    //       path: '/admin/companies/b2b',
    //       badge: 45,
    //       badgeColor: 'success'
    //     },
    //     {
    //       id: 'b2c',
    //       label: 'B2C Companies',
    //       icon: <Globe size={16} />,
    //       path: '/admin/companies/b2c',
    //       badge: 89,
    //       badgeColor: 'warning'
    //     },
    //     {
    //       id: 'contacts',
    //       label: 'All Contacts',
    //       icon: <Users size={16} />,
    //       path: '/admin/companies/contacts'
    //     }
    //   ]
    // },
    // {
    //   id: 'fdc',
    //   label: 'FDC Projects',
    //   icon: <FileText size={20} />,
    //   children: [
    //     {
    //       id: 'projects',
    //       label: 'All Projects',
    //       icon: <FileCode size={16} />,
    //       path: '/admin/fdc/projects',
    //       badge: 23,
    //       badgeColor: 'primary'
    //     },
    //     {
    //       id: 'pending',
    //       label: 'Pending Projects',
    //       icon: <Layers size={16} />,
    //       path: '/admin/fdc/pending',
    //       badge: 5,
    //       badgeColor: 'warning'
    //     }
    //   ]
    // },
    // {
    //   id: 'analytics',
    //   label: 'Analytics',
    //   icon: <PieChart size={20} />,
    //   path: '/admin/analytics'
    // },
    // {
    //   id: 'messages',
    //   label: 'Messages',
    //   icon: <MessageSquare size={20} />,
    //   path: '/admin/messages',
    //   badge: 12,
    //   badgeColor: 'danger'
    // },
    // {
    //   id: 'settings',
    //   label: 'Settings',
    //   icon: <Settings size={20} />,
    //   path: '/admin/settings'
    // }
  ];

  const toggleExpand = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleNavigation = (path?: string, itemId?: string) => {
    if (path) {
      // Every other item here stays under /admin - FDC Projects (and any
      // future item like it) leaves the Admin shell entirely, so it needs a
      // way to signal "I came from Admin" to whatever page it lands on,
      // since that page has no other way to know (it's also reachable from
      // the regular Data Center nav, which shouldn't show an admin-specific
      // back link).
      navigate(path, path.startsWith('/admin') ? undefined : { state: { fromAdmin: true } });
    } else if (itemId) {
      toggleExpand(itemId);
    }
  };

  const isActive = (path?: string): boolean => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.path && isActive(item.path)) return true;
    if (item.children) {
      return item.children.some(child => isItemActive(child));
    }
    return false;
  };

  const getBadgeClass = (color?: string): string => {
    switch (color) {
      case 'primary': return 'badge-primary';
      case 'success': return 'badge-success';
      case 'warning': return 'badge-warning';
      case 'danger': return 'badge-danger';
      default: return 'badge-default';
    }
  };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const isExpanded = expandedItems.has(item.id);
    const itemActive = isItemActive(item);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <React.Fragment key={item.id}>
        <li
          className={`nav-item ${itemActive ? 'active' : ''}`}
          onMouseEnter={() => setHoveredItem(item.id)}
          onMouseLeave={() => setHoveredItem(null)}
          style={{
            paddingLeft: isOpen ? `${depth * 20 + 20}px` : '0',
            justifyContent: isOpen ? 'flex-start' : 'center'
          }}
        >
          <div
            className={`nav-link ${hasChildren ? 'has-children' : ''}`}
            onClick={() => handleNavigation(item.path, hasChildren && !isOpen ? undefined : item.id)}
            title={!isOpen ? item.label : undefined}
          >
            <div className="nav-icon-wrapper">
              <div className={`nav-icon ${itemActive ? 'active' : ''}`}>
                {item.icon}
              </div>

              {isOpen && (
                <div className="nav-content">
                  <span className="nav-label">
                    {item.label}
                    {item.isNew && <span className="new-badge">NEW</span>}
                  </span>

                  {item.badge !== undefined && (
                    <span className={`badge ${getBadgeClass(item.badgeColor)}`}>
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </div>

            {isOpen && hasChildren && (
              <span className="chevron">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            )}

            {/* Hover tooltip for collapsed state */}
            {!isOpen && hoveredItem === item.id && (
              <div className="sidebar-tooltip">
                <div className="tooltip-content">
                  {item.label}
                  {item.badge !== undefined && (
                    <span className={`tooltip-badge ${getBadgeClass(item.badgeColor)}`}>
                      {item.badge}
                    </span>
                  )}
                  {item.isNew && <span className="tooltip-new">NEW</span>}
                </div>
              </div>
            )}
          </div>
        </li>

        {/* Render children if expanded AND sidebar is open */}
        {hasChildren && isExpanded && isOpen && (
          <ul className="submenu">
            {item.children!.map(child => renderMenuItem(child, depth + 1))}
          </ul>
        )}
      </React.Fragment>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>

      {/* Navigation Menu */}
      <nav className="sidebar-nav" onClick={onToggle}>
        <ul className="nav-menu">
          {menuItems.map(item => renderMenuItem(item))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;